import { useQuery } from "react-query";
import { Drawer, Skeleton, Tag } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";

import { PortalApi } from "../../../services";
import { EmptyState } from "../../../components";
import { toFa } from "../../../utils/helpers";
import styles from "../styles.module.css";

/**
 * یک کار، از دید مشتری.
 *
 * فقط‌خواندنی و عمداً کم‌جزئیات: مسئول، تخمین، گفتگوی داخلی تیم و تاریخچه‌ی
 * وضعیت‌ها اینجا نمی‌آیند. آنچه می‌آید همان چیزی است که تیم صریحاً «مشتری هم
 * ببیند» زده — یادداشت‌ها و پیوست‌های علامت‌خورده — به‌علاوه‌ی زیر تسکها که «چه
 * چیزی مانده» را جواب می‌دهند.
 */
const jalali = (value) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : null;

const PortalTaskDrawer = ({ uuid, open, onClose }) => {
  const { data: task, isFetching } = useQuery({
    queryKey: ["[Portal] Task", uuid],
    queryFn: () =>
      PortalApi.getPortalTask({ params: { uuid } }).then((res) => res.data.task),
    enabled: Boolean(uuid) && open,
  });

  return (
    <Drawer open={open} onClose={onClose} width={560} title={task?.title || "کار"}>
      {isFetching && !task ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        task && (
          <>
            <div className={styles.taskMeta}>
              <Tag color={task.is_done ? "green" : "blue"}>
                {task.is_done ? "انجام شده" : task.step || "در جریان"}
              </Tag>
              {jalali(task.due_date) && (
                <span>
                  <ClockCircleOutlined /> مهلت: {jalali(task.due_date)}
                </span>
              )}
              <span>{task.project?.title}</span>
            </div>

            {task.description && (
              <p className={styles.replyBody}>{task.description}</p>
            )}

            {task.subtasks?.length > 0 && (
              <div className="mt-3">
                <div className={styles.sectionLabel}>
                  زیر تسکها ({toFa(task.subtasks.filter((item) => item.is_done).length)} از{" "}
                  {toFa(task.subtasks.length)})
                </div>
                {task.subtasks.map((subtask) => (
                  <div className={styles.task} key={subtask.uuid}>
                    <span className={subtask.is_done ? styles.done : ""}>
                      {subtask.is_done && <CheckCircleOutlined className="ms-1" />}
                      {subtask.title}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {task.attachments?.length > 0 && (
              <div className="mt-3">
                <div className={styles.sectionLabel}>پیوست‌ها</div>
                <div className={styles.files}>
                  {task.attachments.map((file) => (
                    <a
                      key={file.uuid}
                      href={file.original_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <PaperClipOutlined />
                      {file.name}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-3">
              <div className={styles.sectionLabel}>یادداشت‌ها</div>
              {task.notes?.length ? (
                <div className={styles.thread}>
                  {task.notes.map((note) => (
                    <div className={styles.reply} key={note.id}>
                      <div className={styles.replyHead}>
                        <strong>{note.author || "تیم"}</strong>
                        <span>{jalali(note.created_at)}</span>
                      </div>
                      <div className={styles.replyBody}>{note.body}</div>
                      {note.attachments?.length > 0 && (
                        <div className={styles.files}>
                          {note.attachments.map((file) => (
                            <a
                              key={file.uuid}
                              href={file.original_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <PaperClipOutlined />
                              {file.name}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  compact
                  title="یادداشتی برای شما ثبت نشده"
                  description="هر توضیحی که تیم برای شما بگذارد همین‌جا دیده می‌شود."
                />
              )}
            </div>
          </>
        )
      )}
    </Drawer>
  );
};

export default PortalTaskDrawer;
